// The meta profile: what follows the player across saves and devices (achievements, the codex, retired tutorials),
// as one JSON document of versioned slices. A game keeps it beside its settings in its save store and syncs it with a
// merge (`mergeProfiles`): every slice merges without a conflict, so neither device's progress is lost. A slice at an
// older version is migrated one step at a time by the game's migrations; a slice newer than the game knows is refused.

/** The profile document's format. */
export const META_PROFILE_FORMAT = "rpg-meta@1" as const;

/** One slice: its version and data. */
export interface ProfileSlice {
  version: number;
  data: unknown;
}

/** The profile document. */
export interface MetaProfile {
  format: typeof META_PROFILE_FORMAT;
  slices: Record<string, ProfileSlice>;
}

/** A slice's definition: its current version, migrations from each older version to the next, and how two copies
 *  merge. */
export interface ProfileSliceDef<T> {
  version: number;
  migrate?: Readonly<Record<number, (data: unknown) => unknown>>;
  merge(a: T, b: T): T;
  /** A fresh slice (no progress). */
  empty(): T;
}

/** Make a profile from slice data at their current versions. */
export function makeProfile(defs: Readonly<Record<string, ProfileSliceDef<unknown>>>, data: Readonly<Record<string, unknown>>): MetaProfile {
  const slices: Record<string, ProfileSlice> = {};
  for (const [k, d] of Object.entries(defs)) slices[k] = { version: d.version, data: structuredClone(data[k] ?? d.empty()) };
  return { format: META_PROFILE_FORMAT, slices };
}

/** Read a stored profile: each known slice migrated to its current version (a missing one fresh). Throws on a document
 *  that isn't a profile or a slice newer than its definition. */
export function readProfile(defs: Readonly<Record<string, ProfileSliceDef<unknown>>>, raw: unknown): Record<string, unknown> {
  const doc = raw as Partial<MetaProfile> | null;
  if (!doc || doc.format !== META_PROFILE_FORMAT || typeof doc.slices !== "object" || doc.slices === null) throw new Error(`meta profile: not a ${META_PROFILE_FORMAT} document`);
  const out: Record<string, unknown> = {};
  for (const [k, d] of Object.entries(defs)) {
    const s = doc.slices[k];
    if (!s) {
      out[k] = d.empty();
      continue;
    }
    if (s.version > d.version) throw new Error(`meta profile: slice "${k}" is at v${s.version}, newer than this game's v${d.version}`);
    let data = s.data;
    for (let v = s.version; v < d.version; v++) {
      const step = d.migrate?.[v];
      if (!step) throw new Error(`meta profile: slice "${k}" has no migration from v${v}`);
      data = step(data);
    }
    out[k] = data;
  }
  return out;
}

/** Merge two stored profiles slice by slice (a sync with changes on both sides). */
export function mergeProfiles(defs: Readonly<Record<string, ProfileSliceDef<unknown>>>, a: unknown, b: unknown): MetaProfile {
  const x = readProfile(defs, a);
  const y = readProfile(defs, b);
  const merged: Record<string, unknown> = {};
  for (const [k, d] of Object.entries(defs)) merged[k] = d.merge(x[k], y[k]);
  return makeProfile(defs, merged);
}
